import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { LibraryContentList } from 'src/common/components/library/content-list';
import { useFilesUploadStore } from 'src/common/store/files-upload.store';
import { DashboardLibraryContentAddSection } from './add-section';
import { apiCreateOrUpdateNode } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { functionNoop } from 'src/utils/function/noop';
import { AssetResponse } from 'src/common/helpers/file-upload';
import { getContentParamsByAsset } from './helpers/get-content-params-by-asset';
import delay from 'lodash/delay';
import debounce from 'lodash/debounce';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { DashboardLibraryContentAddPanel } from './add-panel';
import { OnboardingGuideHost } from 'src/onboarding/components/guide/host';
import classNames from 'classnames';
import { DropZoneComponent } from 'src/common/components/drop-zone/component';
import { ContentTypeEnum } from 'src/common/interfaces/content/type.enum';
import { useEmbedUrlChange } from 'src/editor/step-editor/use-embed-url-change.hook';
import { useStepEditorContext } from 'src/store/step-editor';

export const REMOVE_STACK_DELAY_IN_MS = 600;

export const DashboardLibraryContent = () => {
  const fetchContentRef = useRef(functionNoop);
  const filesToUploadLengthRef = useRef(0);
  const uploadedFilesLengthRef = useRef(0);
  const { currentOrganization, getMemberWorkspaces } = useCurrentOrganization();
  const { currentUser } = useCurrentUser();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const memberWorkspaces = getMemberWorkspaces(currentUser);

  const setCheckEmbedUrlFromLibrary = useStepEditorContext((state) => state.setCheckEmbedUrlFromLibrary);

  const contentPanelOpenRef = useRef(functionNoop);
  const contentPanelCloseRef = useRef(functionNoop);

  const [publicWorkspace] = memberWorkspaces.filter(({ workspace_type }) => workspace_type === 'public');

  const { addUploadStack, removeUploadStack, startUpload, stackLength } = useFilesUploadStore();

  const { checkEmbedUrl } = useEmbedUrlChange();

  const onAddContentClicked = () => {
    contentPanelOpenRef.current();
  };

  const debounceFetchListHandler = debounce(() => {
    fetchContentRef.current();
  }, REMOVE_STACK_DELAY_IN_MS);

  useEffect(() => {
    return () => {
      debounceFetchListHandler.cancel();
    };
  }, [debounceFetchListHandler]);

  const onContentCreated = () => {
    popContentAddNotification();
    debounceFetchListHandler();
  };

  const popContentAddNotification = () => {
    setSuccessNotification('Content added to library', { pauseOnHover: false });
  };

  const popErrorNotification = () => {
    setErrorNotification('Something went wrong');
  };

  const onUploadSuccess = async (asset: AssetResponse, tags?: { id: number; name: string }[]) => {
    const { contentType, payload } = getContentParamsByAsset(asset);

    try {
      const content = await apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, contentType, null, {
        ...payload,
        tag_ids: tags?.map((tag) => tag.id),
      });

      delay(() => {
        uploadedFilesLengthRef.current += 1;
        debounceFetchListHandler();
        if (filesToUploadLengthRef.current === uploadedFilesLengthRef.current) {
          popContentAddNotification();
        }

        removeUploadStack(asset.name);
      }, REMOVE_STACK_DELAY_IN_MS);
    } catch (error) {
      popErrorNotification();
    } finally {
      delay(() => {
        removeUploadStack(asset.name);
      }, REMOVE_STACK_DELAY_IN_MS);
    }
  };

  const onUploadCancel = (name: string) => {
    delay(() => removeUploadStack(name), REMOVE_STACK_DELAY_IN_MS);
  };

  const initializeUploads = (files: File[], tags?: { id: number; name: string }[]) => {
    addUploadStack(files);
    filesToUploadLengthRef.current += files.length;
    startUpload({
      onSuccess: (asset) => onUploadSuccess(asset, tags),
      onCancel: onUploadCancel,
    });
  };

  const createUrlContent = async (name: string, url: string, tags?: { id: number; name: string }[]) => {
    try {
      const content = await apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, 'embed', null, {
        name,
        url,
        tag_ids: tags?.map((tag) => tag.id),
      });

      onContentCreated();
    } catch (error) {
      popErrorNotification();
    }
  };

  const onSubmit = ({ url, files, tags }: { url?: string; files?: File[]; tags?: { id: number; name: string }[] }) => {
    if (url) {
      createUrlContent(url, url, tags);
    }
    if (files && files.length > 0) {
      initializeUploads(files, tags);
    }
  };

  const createEmbedContent = (name: string, url: string) => {
    return apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, ContentTypeEnum.EMBED, null, {
      name,
      url,
    }).then(onContentCreated);
  };

  const onGoogleDriveFileSelected = ({ name, embedUrl }: { name: string; embedUrl: string }) => {
    createEmbedContent(name, embedUrl).catch(popErrorNotification);
  };

  const createExternalLinkBoxContent = (url: string) => {
    return checkEmbedUrl(url, { isLibrary: true })
      .then(onContentCreated)
      .then(() => {
        setCheckEmbedUrlFromLibrary(false);
      });
  };

  return (
    <>
      <Helmet>
        <title>Journey | Content</title>
      </Helmet>
      <DropZoneComponent
        containerClassName='min-w-[990px]'
        removeBorder={false}
        dragZoneClassName='h-[calc(100vh-278px)]'
        onFileDrag={() => {
          contentPanelCloseRef.current();
        }}
        onFileDrop={(files) => {
          initializeUploads(files);
        }}
      >
        {(dragging) => (
          <>
            <div
              className={classNames('w-full transition-opacity', {
                'opacity-0': dragging,
                'opacity-100': !dragging,
              })}
            >
              <LibraryContentList
                containerClassName={classNames('h-full transform-opacity', dragging ? 'opacity-0' : 'opacity-100')}
                disableRowCursorPointer
                loadingClassName='w-[calc(100%-320px)] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                tableClassName='max-w-[649px] 2k:max-w-[808px] w-full'
                // contentContainerClassName='overflow-hidden'
                refetchRef={fetchContentRef}
                editable
                removable
              />
            </div>
            <DashboardLibraryContentAddSection
              onSelectFileFromGoogleDrive={onGoogleDriveFileSelected}
              onAddContentClick={onAddContentClicked}
              onAddBoxContentClick={createExternalLinkBoxContent}
            />
            {dragging && stackLength === 0 && (
              <OnboardingGuideHost show title='Go on - drop to upload' titleClassName={null} content={null} />
            )}
          </>
        )}
      </DropZoneComponent>
      <DashboardLibraryContentAddPanel
        onSubmit={onSubmit}
        panelCloseRef={contentPanelCloseRef}
        panelOpenRef={contentPanelOpenRef}
      />
    </>
  );
};
